import { inject, Injectable } from '@angular/core';
import { debounce, isArray, without } from 'lodash-es';
import { DateTime } from 'luxon';
import { catchError, concat, defer, last, merge, Observable, of, Subscription, switchMap, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PluginApi } from '../model/plugin';
import { Ref } from '../model/ref';
import { Action, DownloadAction, downloadModel, EmitAction, emitModels } from '../model/tag';
import { Store } from '../store/store';
import { merge3 } from '../util/diff';
import { downloadRef, downloadTag, file, saveAs } from '../util/download';
import { hasTag } from '../util/tag';
import { ExtService } from './api/ext.service';
import { RefService } from './api/ref.service';
import { StompService } from './api/stomp.service';
import { TaggingService } from './api/tagging.service';

@Injectable({
  providedIn: 'root'
})
export class ActionService {
  private refs = inject(RefService);
  private exts = inject(ExtService);
  private tags = inject(TaggingService);
  private store = inject(Store);
  private stomp = inject(StompService);


  wrap(ref?: Ref): PluginApi {
    let o: Subscription | undefined = undefined;
    return {
      comment: debounce((comment: string) => {
        if (!ref) throw 'Error: No ref to save';
        o?.unsubscribe();
        o = this.comment$(comment, ref).subscribe();
      }, 500),
      patch: (patch: Partial<Ref>) => {
        if (!ref) throw 'Error: No ref to save';
        this.patch(patch, ref);
      },
      event: (event: string) => {
        this.event(event, ref);
      },
      emit: (a: EmitAction) => {
        this.emit(a, ref);
      },
      download: (a: DownloadAction) => {
        this.download(a, ref);
      },
      tag: (tag: string) => {
        if (!ref) throw 'Error: No ref to tag';
        this.tag(tag, ref);
      },
      respond: (response: string, clear?: string[]) => {
        if (!ref) throw 'Error: No ref to respond to';
        this.respond(response, clear || [], ref);
      },
      watch: (delimiter?: string) => {
        if (!ref) throw 'Error: No ref to respond to';
        return this.watch(ref, delimiter);
      },
      append: () => {
        if (!ref) throw 'Error: No ref to respond to';
        return this.append(ref);
      }
    }
  }

  apply$(actions: Action | Action[], ref: Ref, repost?: Ref) {
    if (!isArray(actions)) actions = [actions];
    const updates: Observable<any>[] = [];
    for (const a of actions) {
      if ('tag' in a) {
        updates.push(this.tag$(a.tag, ref));
      }
      if ('response' in a) {
        updates.push(this.respond$(a.response, a.clear || [], ref));
      }
      if ('event' in a) {
        updates.push(this.event$(a.event, ref, repost));
      }
      if ('emit' in a) {
        updates.push(this.emit$(a, ref));
      }
      if ('download' in a) {
        updates.push(this.download$(a, ref));
      }
    }
    if (!updates.length) return of(null);
    return this.store.eventBus.runAndReload$(concat(...updates).pipe(last()), ref);
  }

  event(event: string, ref?: Ref, repost?: Ref) {
    this.store.eventBus.fire(event, ref, repost);
  }

  event$(event: string, ref?: Ref, repost?: Ref) {
    this.store.eventBus.fire(event, ref, repost);
    return of(null);
  }

  emit(a: EmitAction, ref?: Ref) {
    this.emit$(a, ref).subscribe();
  }

  emit$(a: EmitAction, ref?: Ref) {
    const models = emitModels(a, ref, this.store.account.localTag());
    const uploads = [
      ...models.ref.map(ref => this.refs.create(ref)),
      ...models.ext.map(ext => this.exts.create(ext)),
    ];
    return concat(...uploads).pipe(last());
  }

  download(a: DownloadAction, ref?: Ref) {
    this.download$(a, ref).subscribe();
  }

  download$(a: DownloadAction, ref?: Ref) {
    return defer(() => {
      const model = downloadModel(a, ref, this.store.account.localTag());
      if (model?.url) {
        downloadRef(model);
      } else if (model?.tag) {
        downloadTag(model);
      } else {
        saveAs(file(model), 'download.json');
      }
      return of(null);
    });
  }

  comment(comment: string, ref: Ref) {
    this.store.eventBus.runAndRefresh(this.comment$(comment, ref), ref);
  }

  comment$(comment: string, ref: Ref) {
    return this.refs.patch(ref.url, this.store.account.origin(), ref.modifiedString!, [{
      op: 'add',
      path: '/comment',
      value: comment,
    }]).pipe(
      catchError(err => {
        if (err.status === 409) {
          return this.refs.get(ref.url, this.store.account.origin()).pipe(
            switchMap(ref => this.refs.patch(ref.url, this.store.account.origin(), ref.modifiedString!, [{
              op: 'add',
              path: '/comment',
              value: comment,
            }])),
          );
        }
        return throwError(() => err);
      }),
      tap(cursor => {
        ref.comment = comment;
        ref.modifiedString = cursor;
        ref.modified = DateTime.fromISO(cursor);
      }),
    );
  }

  patch(patch: Partial<Ref>, ref: Ref) {
    this.store.eventBus.runAndRefresh(this.patch$(patch, ref), ref);
  }

  patch$(patch: Partial<Ref>, ref: Ref): Observable<string> {
    const pluginTags = Object.keys(patch.plugins || {});
    const save = (target: Ref) => {
      const tags = patch.tags || target.tags;
      const missingTags = pluginTags.filter(t => !hasTag(t, tags));
      const update = missingTags.length ? { ...patch, tags: [...(tags || []), ...missingTags] } : patch;
      return this.refs.merge(target.url, this.store.account.origin(), target.modifiedString!, update);
    };
    return save(ref).pipe(
      catchError(err => {
        if (err.status === 409) {
          return this.refs.get(ref.url, this.store.account.origin()).pipe(switchMap(save));
        }
        return throwError(() => err);
      }),
      tap(cursor => {
        const plugins = patch.plugins ? { ...ref.plugins, ...patch.plugins } : ref.plugins;
        Object.assign(ref, patch);
        if (patch.plugins) ref.plugins = plugins;
        const newTags = pluginTags.filter(t => !hasTag(t, ref));
        if (newTags.length) ref.tags = [...(ref.tags || []), ...newTags];
        ref.modifiedString = cursor;
        ref.modified = DateTime.fromISO(cursor);
      }),
    );
  }

  tag(tag: string, ref: Ref) {
    this.store.eventBus.runAndReload(this.tag$(tag, ref), ref);
  }

  tag$(tag: string, ref: Ref) {
    const patch = (hasTag(tag, ref) ? '-' : '') + tag;
    return this.tags.create(patch, ref.url, this.store.account.origin());
  }

  respond(response: string, clear: string[], ref: Ref) {
    this.store.eventBus.runAndRefresh(this.respond$(response, clear, ref), ref);
  }

  respond$(response: string, clear: string[], ref: Ref) {
    if (ref.metadata?.userUrls?.includes(response)) {
      ref.metadata ||= {};
      ref.metadata.userUrls ||= [];
      ref.metadata.userUrls = without(ref.metadata.userUrls, response);
      return this.tags.deleteResponse(response, ref.url);
    } else {
      const tags = [
        ...clear.map(t => '-' + t),
        response,
      ];
      ref.metadata ||= {};
      ref.metadata.userUrls ||= [];
      ref.metadata.userUrls.push(response);
      ref.metadata.userUrls = without(ref.metadata.userUrls, ...clear);
      return this.tags.respond(tags, ref.url);
    }
  }

  watch(ref: Ref, delimiter = '\n') {
    let cursor = ref.origin === this.store.account.origin() ? ref.modifiedString! : '';
    let baseComment = ref.comment || '';
    const inner = {
      ref$: merge(...this.store.origins.list().map(origin => this.stomp.watchRef(ref.url, origin).pipe(
        tap(u => {
          if (u.origin === this.store.account.origin()) cursor = u.modifiedString!;
          baseComment = u.comment || '';
        }),
      ))),
      comment$: (comment: string): Observable<string> => {
        if (!cursor) {
          return this.refs.get(ref.url, this.store.account.origin()).pipe(
            tap(ref => {
              cursor = ref.modifiedString!;
              baseComment = ref.comment || '';
            }),
            switchMap(ref => inner.comment$(comment)),
          );
        }
        return this.refs.patch(ref.url, this.store.account.origin(), cursor, [{
          op: 'add',
          path: '/comment',
          value: comment,
        }]).pipe(
          tap(c => {
            cursor = c;
            baseComment = comment;
          }),
          catchError(err => {
            if (err.status === 409) {
              // Fetch the current version from server
              return this.refs.get(ref.url, this.store.account.origin()).pipe(
                switchMap(remote => {
                  const { result, conflict } = merge3(comment, baseComment, remote.comment || '', delimiter);
                  cursor = remote.modifiedString!;
                  baseComment = remote.comment || '';
                  if (conflict) return throwError(() => ({ conflict }));
                  return inner.comment$(result || '');
                }),
              );
            }
            return throwError(() => err);
          }),
        );
      },
    };
    return inner;
  }

  append(ref: Ref) {
    let cursor = ref.origin === this.store.account.origin() ? ref.modifiedString! : '';
    let comment = ref.comment || '';
    const inner = {
      updates$: merge(...this.store.origins.list().map(origin => this.stomp.watchRef(ref.url, origin).pipe(
        tap(u => {
          if (u.origin === this.store.account.origin()) cursor = u.modifiedString!;
        }),
        switchMap(u => {
          if (comment.startsWith(u?.comment || '')) return of()
          if (!u.comment?.startsWith(comment)) {
            comment = u.comment || '';
            throw u;
          }
          const moves = u.comment.substring(comment.length).split('\n').map(m => m.trim()).filter(m => !!m);
          comment = comment ? `${comment}  \n${moves.join('  \n')}` : moves.join('  \n');
          return moves;
        })
      ))),
      append$: (value: string): Observable<string> => {
        if (!cursor) {
          return this.refs.get(ref.url, this.store.account.origin()).pipe(
            tap(ref => cursor = ref.modifiedString!),
            switchMap(ref => inner.append$(value)),
          );
        }
        comment = comment ? `${comment}  \n${value}` : value;
        return this.refs.patch(ref.url, this.store.account.origin(), cursor, [{
          op: 'add',
          path: '/comment',
          value: comment,
        }]).pipe(
          tap(c => cursor = c),
        );
      },
      reset$: (value: string[]): Observable<string> => {
        if (!cursor) {
          return this.refs.get(ref.url, this.store.account.origin()).pipe(
            tap(ref => cursor = ref.modifiedString!),
            switchMap(ref => inner.reset$(value)),
          );
        }
        comment = value.join('  \n');
        return this.refs.patch(ref.url, this.store.account.origin(), cursor, [{
          op: 'add',
          path: '/comment',
          value: comment,
        }]).pipe(
          tap(c => cursor = c),
        );
      },
    };
    return inner;
  }
}
